import {
  Button,
  Checkbox,
  Chip,
  FormControl,
  FormControlLabel,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography,
  makeStyles,
} from '@material-ui/core';
import ClearAllIcon from '@material-ui/icons/ClearAll';
import { ActivityFilters } from '../../api/types';
import type { ActivityDateDraft } from './activityFilterState';
import type {
  FilterControlsProps,
  SelectOption,
} from './ActivityPrimaryFilters';

const useStyles = makeStyles(theme => ({
  toolbar: {
    display: 'flex',
    flexWrap: 'wrap',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: theme.spacing(1),
    marginTop: theme.spacing(1),
  },
  toolbarGroup: {
    display: 'flex',
    flexWrap: 'wrap',
    alignItems: 'center',
    gap: theme.spacing(1),
  },
  moreButton: { whiteSpace: 'nowrap' },
  countChip: { marginLeft: theme.spacing(0.75) },
  advanced: { paddingTop: theme.spacing(1.5) },
  error: { marginTop: theme.spacing(1) },
}));

interface FilterSelectProps {
  id: string;
  label: string;
  value?: string;
  options: SelectOption[];
  allLabel: string;
  disabled?: boolean;
  onChange: (value: string | undefined) => void;
}

export const FilterSelect = ({
  id,
  label,
  value,
  options,
  allLabel,
  disabled,
  onChange,
}: FilterSelectProps) => (
  <FormControl variant="outlined" size="small" fullWidth disabled={disabled}>
    <InputLabel id={`${id}-label`}>{label}</InputLabel>
    <Select
      labelId={`${id}-label`}
      label={label}
      value={value ?? ''}
      onChange={event => onChange((event.target.value as string) || undefined)}
    >
      <MenuItem value="">{allLabel}</MenuItem>
      {options.map(option => (
        <MenuItem key={option.value} value={option.value}>
          {option.label}
        </MenuItem>
      ))}
    </Select>
  </FormControl>
);

interface FilterToolbarProps {
  filters: ActivityFilters;
  expanded: boolean;
  advancedCount: number;
  clearDisabled: boolean;
  onExpandedChange: (expanded: boolean) => void;
  onFiltersChange: (patch: Partial<ActivityFilters>) => void;
  onReset: () => void;
}

export const FilterToolbar = (props: FilterToolbarProps) => {
  const classes = useStyles();
  return (
    <div className={classes.toolbar}>
      <div className={classes.toolbarGroup}>
        <FormControlLabel
          control={
            <Checkbox
              checked={props.filters.mineOnly ?? true}
              onChange={event =>
                props.onFiltersChange({ mineOnly: event.target.checked })
              }
              color="primary"
              size="small"
            />
          }
          label={
            <Typography variant="body2">Apenas minhas atividades</Typography>
          }
        />
        <Button
          size="small"
          className={classes.moreButton}
          aria-expanded={props.expanded}
          aria-controls="advanced-activity-filters"
          onClick={() => props.onExpandedChange(!props.expanded)}
        >
          Mais filtros
          {props.advancedCount > 0 && (
            <Chip
              size="small"
              className={classes.countChip}
              label={props.advancedCount}
              aria-label={`${props.advancedCount} filtros avançados ativos`}
            />
          )}
        </Button>
      </div>
      <Button
        size="small"
        startIcon={<ClearAllIcon fontSize="small" />}
        onClick={props.onReset}
        disabled={props.clearDisabled}
        aria-label="Limpar filtros"
      >
        Limpar filtros
      </Button>
    </div>
  );
};

interface DateFieldProps {
  id: string;
  label: string;
  value: string;
  errorId?: string;
  onChange: (value: string) => void;
}

const DateField = ({ id, label, value, errorId, onChange }: DateFieldProps) => (
  <TextField
    id={id}
    label={label}
    type="date"
    variant="outlined"
    size="small"
    fullWidth
    value={value}
    onChange={event => onChange(event.target.value)}
    InputLabelProps={{ shrink: true }}
    inputProps={{ 'aria-describedby': errorId }}
  />
);

interface AdvancedFiltersProps extends FilterControlsProps {
  issueInput: string;
  onIssueInputChange: (value: string) => void;
  dateDraft: ActivityDateDraft;
}

function AdvancedFields(props: AdvancedFiltersProps & { errorId?: string }) {
  const creatorOptions = props.options.creators.map(value => ({
    value,
    label: props.getCreatorLabel(value),
  }));
  return (
    <Grid container spacing={2}>
      <Grid item xs={12} sm={6} md={3}>
        <TextField
          id="filter-issue"
          label="Issue"
          variant="outlined"
          size="small"
          fullWidth
          value={props.issueInput}
          onChange={event => props.onIssueInputChange(event.target.value)}
        />
      </Grid>
      <Grid item xs={12} sm={6} md={3}>
        <FilterSelect
          id="filter-created-by"
          label="Criado por"
          value={props.filters.createdBy}
          options={creatorOptions}
          allLabel="Todos"
          disabled={props.loadingOptions && creatorOptions.length === 0}
          onChange={createdBy => props.onFiltersChange({ createdBy })}
        />
      </Grid>
      <Grid item xs={12} sm={6} md={3}>
        <DateField
          id="filter-created-from"
          label="De"
          value={props.dateDraft.fromInput}
          errorId={props.errorId}
          onChange={props.dateDraft.setFromInput}
        />
      </Grid>
      <Grid item xs={12} sm={6} md={3}>
        <DateField
          id="filter-created-to"
          label="Até"
          value={props.dateDraft.toInput}
          errorId={props.errorId}
          onChange={props.dateDraft.setToInput}
        />
      </Grid>
    </Grid>
  );
}

export const AdvancedFilters = (props: AdvancedFiltersProps) => {
  const classes = useStyles();
  const errorMessage = props.dateDraft.range.valid
    ? undefined
    : props.dateDraft.range.error;
  const errorId = errorMessage ? 'activity-date-range-error' : undefined;
  return (
    <div className={classes.advanced}>
      <AdvancedFields {...props} errorId={errorId} />
      {errorId && (
        <Typography
          id={errorId}
          role="alert"
          color="error"
          variant="caption"
          className={classes.error}
        >
          {errorMessage}
        </Typography>
      )}
    </div>
  );
};
